import html2canvas from 'html2canvas'
import { captureRecentRequests, generateHARFile } from '@/shared/services/network-recorder.service'

function formatTimestamp() {
  const date = new Date()
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return `${day}-${month}-${year}_${hours}h-${minutes}m-${seconds}s`
}

function base64ToBlob(base64Data, defaultMimeType = 'application/octet-stream') {
  const [meta, content = ''] = String(base64Data || '').split(',')
  const mimeMatch = /data:(.*?);base64/.exec(meta || '')
  const mimeType = mimeMatch?.[1] || defaultMimeType
  const binaryData = atob(content)
  const byteArray = new Uint8Array(binaryData.length)

  for (let index = 0; index < binaryData.length; index += 1) {
    byteArray[index] = binaryData.charCodeAt(index)
  }

  return new Blob([byteArray], { type: mimeType })
}

async function createZipFile(files = []) {
  const JSZip = await import('jszip')
  const ZipConstructor = JSZip.default
  const zip = new ZipConstructor()

  files.forEach((file) => {
    zip.file(file.name, file.content)
  })

  return zip.generateAsync({ type: 'blob' })
}

function downloadBlob(blob, filename) {
  const blobUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(blobUrl)
}

function isValidScreenshot(screenshot) {
  return Boolean(screenshot) && screenshot !== 'data:,'
}

function hasChatSignature(value = '') {
  const text = String(value || '').toLowerCase()
  return /(chat|message|messenger|intercom|zendesk|drift|freshchat|crisp|tawk|whatsapp|suporte|support|help|ajuda)/.test(
    text
  )
}

function isFloatingChatWidget(element) {
  if (!(element instanceof Element)) return false

  const tagName = element.tagName?.toLowerCase()
  const id = element.id || ''
  const className = typeof element.className === 'string' ? element.className : ''
  const ariaLabel = element.getAttribute('aria-label') || ''
  const title = element.getAttribute('title') || ''
  const name = element.getAttribute('name') || ''
  const dataTestId = element.getAttribute('data-testid') || ''
  const attributesText = `${id} ${className} ${ariaLabel} ${title} ${name} ${dataTestId}`

  if (hasChatSignature(attributesText)) return true

  if (tagName === 'iframe' && hasChatSignature(element.getAttribute('src'))) {
    return true
  }

  const style = window.getComputedStyle(element)
  const isFloating = style.position === 'fixed' || style.position === 'sticky'
  const rect = element.getBoundingClientRect()
  const isSmallLauncher = rect.width <= 140 && rect.height <= 140
  const isBottomRight = rect.bottom >= window.innerHeight - 80 && rect.right >= window.innerWidth - 80

  if (!isFloating || !isSmallLauncher || !isBottomRight) return false

  return hasChatSignature(element.textContent || '')
}

function getCaptureDimensions() {
  const allElements = Array.from(document.body.querySelectorAll('*'))
  let largestHeight = document.body.scrollHeight

  allElements.forEach((element) => {
    const rect = element.getBoundingClientRect()
    const isVisible = rect.width > 0 && rect.height > 0
    if (isVisible && element.scrollHeight > largestHeight) {
      largestHeight = element.scrollHeight
    }
  })

  return {
    windowWidth: document.documentElement.scrollWidth,
    windowHeight: largestHeight
  }
}

function getScreenshotBackgroundColor() {
  const fallbackColor = '#eef2f7'
  const candidates = [
    document.querySelector('#app'),
    document.querySelector('.md-app'),
    document.body,
    document.documentElement
  ].filter(Boolean)

  for (const candidate of candidates) {
    const backgroundColor = window.getComputedStyle(candidate).backgroundColor
    if (backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent') {
      return backgroundColor
    }
  }

  return fallbackColor
}

function createCaptureOptions(debugCaptureRoot, captureDimensions, foreignObjectRendering = false) {
  const dpr = window.devicePixelRatio || 1
  const captureScale = Math.min(3, Math.max(2, dpr))
  const screenshotBackgroundColor = getScreenshotBackgroundColor()

  return {
    useCORS: true,
    backgroundColor: screenshotBackgroundColor,
    scrollX: -window.scrollX,
    scrollY: -window.scrollY,
    windowWidth: captureDimensions.windowWidth,
    windowHeight: captureDimensions.windowHeight,
    scale: captureScale,
    foreignObjectRendering,
    onclone: (clonedDocument) => {
      const htmlElement = clonedDocument.documentElement
      const bodyElement = clonedDocument.body
      const minimumHeight = `${captureDimensions.windowHeight}px`

      htmlElement.style.backgroundColor = screenshotBackgroundColor
      bodyElement.style.backgroundColor = screenshotBackgroundColor
      htmlElement.style.minHeight = minimumHeight
      bodyElement.style.minHeight = minimumHeight

      const iconElements = clonedDocument.querySelectorAll('.md-icon, i.material-icons')
      iconElements.forEach((iconElement) => {
        iconElement.style.fontFamily = 'Material Icons'
        iconElement.style.fontWeight = 'normal'
        iconElement.style.fontStyle = 'normal'
      })
    },
    ignoreElements: (element) => {
      const isDebugCaptureElement = Boolean(
        debugCaptureRoot && (element === debugCaptureRoot || debugCaptureRoot.contains(element))
      )

      return isDebugCaptureElement || isFloatingChatWidget(element)
    }
  }
}

async function tryCaptureWithOptions(options) {
  const canvas = await html2canvas(document.body, options)
  return canvas.toDataURL('image/png')
}

async function captureScreenshot() {
  const debugCaptureRoot = document.querySelector('[data-debug-capture="true"]')
  const captureDimensions = getCaptureDimensions()

  const captureStrategies = [
    () => tryCaptureWithOptions(createCaptureOptions(debugCaptureRoot, captureDimensions, false)),
    () => tryCaptureWithOptions(createCaptureOptions(debugCaptureRoot, captureDimensions, true))
  ]

  for (const captureStrategy of captureStrategies) {
    try {
      const screenshot = await captureStrategy()
      if (isValidScreenshot(screenshot)) {
        return screenshot
      }
    } catch (error) {
      if (captureStrategy === captureStrategies[captureStrategies.length - 1]) {
        throw error
      }
    }
  }

  return 'data:,'
}

export default {
  name: 'debugCapture',
  data() {
    return {
      isLoading: false,
      isPanelOpen: false
    }
  },
  methods: {
    openPanel() {
      this.isPanelOpen = true
    },
    closePanel() {
      if (this.isLoading) return
      this.isPanelOpen = false
    },
    async handleErrorCapture() {
      if (this.isLoading) return

      this.isLoading = true

      try {
        let screenshot = await captureScreenshot()

        if (!isValidScreenshot(screenshot)) {
          screenshot = await captureScreenshot()
        }

        const capturedRequests = captureRecentRequests()
        const harContent = capturedRequests.length > 0 ? generateHARFile(capturedRequests) : ''

        if (!isValidScreenshot(screenshot) && !harContent) {
          this.$root.$emit('toast', {
            message: this.$i18n.t('error.unexpected_error_occurred'),
            type: 'error'
          })
          return
        }

        const timestamp = formatTimestamp()
        const files = []

        if (isValidScreenshot(screenshot)) {
          files.push({
            name: `print_error_${timestamp}.png`,
            content: base64ToBlob(screenshot, 'image/png')
          })
        }

        if (harContent) {
          files.push({
            name: `requisicoes_${timestamp}.har`,
            content: harContent
          })
        }

        const zipBlob = await createZipFile(files)
        downloadBlob(zipBlob, `telediagnostico_portal_error_${timestamp}.zip`)

        this.$root.$emit('toast', {
          message: 'Erro capturado e ZIP baixado com sucesso.',
          type: 'success'
        })
      } catch (error) {
        this.$root.$emit('toast', {
          message: this.$i18n.t('error.unexpected_error_occurred'),
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }
    }
  }
}
